import * as THREE from 'three';
import { WEAPONS, LOADOUT, ALL_WEAPONS, shotInterval } from './weapons.data.js';
import { Ballistics } from './Ballistics.js';
import { ViewModel } from './ViewModel.js';
import { createAttachments, behaviourPatch } from './attachments/index.js';

/**
 * Weapon state machine: inventory, ammunition, fire control, recoil bookkeeping.
 *
 * The division of labour is deliberate. This module decides *whether* a shot happens
 * and *what it costs*; `Ballistics` decides where it lands; `ViewModel` decides what
 * it looks like. Nothing here writes to the camera — recoil is published as
 * `weapon:recoil` and the camera rig owns the response, which is what keeps aim-kick
 * and view-shake from contaminating each other. (`_publishRecoil` does *read* one
 * value back off the rig; the comment there explains why, and why reading it beats
 * copying the constant it is derived from.)
 *
 * Recoil is deterministic first and random second. Every shot index maps to a fixed
 * entry in the weapon's pattern table and only a small fraction of the magnitude is
 * noise, so a player who learns to pull down-and-left through the middle of an AR
 * magazine is genuinely rewarded for it.
 *
 * That only works if the pattern *accumulates*. The table holds per-shot increments,
 * so the muzzle's visible path is their running sum; if the aim springs most of the
 * way back between two shots the sum never builds and the shape collapses into a
 * per-shot twitch of a third of a degree. This module therefore owns the aim-recoil
 * curve itself (`aimPitch`/`aimYaw`) and publishes deltas onto it — see
 * `_publishRecoil` for why it is done that way rather than as a fire-and-forget
 * impulse.
 */

export const TUNING = {
  // How long after the last shot the recoil pattern rewinds to the start.
  patternResetTime: 0.34,
  // Grace period after releasing sprint before the weapon can fire.
  sprintOutSlack: 0.02,
  autoReloadOnEmpty: true,
  // Shotgun reloads can be cut short by pulling the trigger.
  interruptibleReload: true,
  inspectKey: 'KeyX',

  /* ------------------------------ aim recoil ------------------------------- */
  // How long after a shot the climb is held before it starts returning. Scaled off
  // the weapon's own shot interval so a 340 RPM marksman rifle still stacks between
  // rounds, then clamped so a pump shotgun still settles between shells.
  recoilHoldShots: 1.35,
  recoilHoldMin: 0.14,
  recoilHoldMax: 0.30,
  // Return rate once the hold expires, 1/s. A full rifle mag settles in ~0.8 s.
  recoilRecovery: 7.0,

  /* -------------------------------- loadout -------------------------------- */
  // Time the weapon is out of action when a loadout swap changes what is in hand.
  // Falls back to the incoming weapon's own draw time; this only floors it.
  loadoutDrawMin: 0.30,
  // Key that steadies a scoped weapon's idle sway (LeftShift, via the sprint
  // binding). Sprint is `wantSprint && moving`, so holding it while stationary and
  // scoped cannot stow the weapon.
  holdBreathAction: 'sprint',
};

const STATE = {
  IDLE: 'idle',
  RELOADING: 'reloading',
  SWITCHING: 'switching',
  PUMPING: 'pumping',
  // A manually-worked bolt. Separate from PUMPING because a pump ejects on the
  // forend and a bolt does not, and because the two have completely different
  // animation hooks — sharing one state would mean branching on `fireMode` in five
  // places instead of one.
  CYCLING: 'cycling',
};

const _origin = new THREE.Vector3();
const _dir = new THREE.Vector3();
const _muzzle = new THREE.Vector3();

export class WeaponSystem {
  constructor(ctx, player) {
    this.ctx = ctx;
    this.player = player ?? ctx.player;
    this.events = ctx.events;
    this.input = ctx.input;

    this.ballistics = new Ballistics(ctx);

    /**
     * The gunsmith.
     *
     * This is the only thing in this module that knows attachments exist, and it
     * knows it in one line. Everywhere else, an inventory slot's `def` is *the*
     * weapon definition — same shape, same fields, same units as `WEAPONS[id]` — and
     * whether five parts went into producing it is not this module's business. That
     * is the whole point of putting the resolver behind `effective()`: the fire path
     * has no modifier logic in it and cannot grow any.
     */
    this.attachments = createAttachments(ctx);
    ctx.attachments = this.attachments;

    /**
     * Set true by the behaviour systems module to stop the weapon firing without
     * touching this module's state machine — an overheat lockout, a stun, anything
     * that is a *reason* not to fire rather than a state of the weapon. Checked in
     * `_handleFire` and nowhere else.
     */
    this.fireInhibit = false;

    /** The `ctx.behaviours` instance this module has already configured, if any. */
    this._behaviourSys = null;

    /* ------------------------------- inventory ------------------------------ */
    this.slots = LOADOUT.map((id) => this._makeSlot(id));
    this.index = 0;

    /* ------------------------------ fire control ---------------------------- */
    this.state = STATE.IDLE;
    this.stateUntil = 0;
    this.nextShotAt = 0;
    this.sprintOutUntil = 0;
    this.lastShotAt = -99;
    this.burstLeft = 0;
    this.burstNextAt = 0;
    this.semiLatch = false;
    this.reloadPhase = null;
    // A shot the player asked for while a shell-by-shell reload was still running.
    this.pendingFire = false;
    this.switchTarget = -1;
    this.switchSwapAt = 0;
    this.switchSwapped = true;
    this.switchIn = 0;
    this.switchFrom = null;
    this._announced = false;
    this._time = 0;

    /* ------------------------------ aim recoil ------------------------------ */
    // The authoritative aim-recoil offset. The camera rig renders it; this owns it.
    this.aimPitch = 0;
    this.aimYaw = 0;

    /* -------------------------------- aim sway ------------------------------ */
    // Scoped idle sway, kept strictly apart from `aimPitch` so the recoil pattern
    // stays measurable: the pattern accumulator is pure, and sway is only folded in
    // where the two are published to the rig together.
    this.swayPitch = 0;
    this.swayYaw = 0;
    this.swayPhase = 0;
    this.swayScale = 1;
    this.breathLeft = 1; // 0..1 of the weapon's own `holdTime`
    this.breathHeld = false;
    this.gaspUntil = -99;

    // A loadout published before this module existed would otherwise be lost, and a
    // loadout published after it does is the normal path. Both land here.
    this._offLoadout = this.events.on('loadout:set', (p) => {
      if (!p) return;
      this.setLoadout(p.primary, p.secondary);
    });
    this._adoptedLoadout = false;

    // A build change re-resolves in place; if the weapon it changed is one we are
    // carrying, everything downstream has to be told the numbers moved.
    this._offAttachments = this.events.on('attachments:set', (p) => {
      if (p?.weapon) this._onBuildChanged(p.weapon);
    });

    // Runs in 'sim', which is upstream of both the camera rig and the viewmodel
    // ('fx'). A shot fired this frame therefore reaches `weapon:recoil` in time for
    // the rig to apply it on the same frame, and reaches the viewmodel in time to be
    // animated on the same frame — neither may be a frame behind the trigger.
    ctx.engine.registerUpdate((dt, t) => this.update(dt, t), 'sim');
    this.viewModel = new ViewModel(ctx, this.player);

    this._applyWeaponSettings();
    this._resolveModels();
    this.viewModel.setWeapon(this.current.def);
    // Build and compile every weapon now rather than on the first switch or the
    // first shot; both would otherwise hitch in the middle of a fight.
    //
    // *Every* weapon, not just the carried ones: with a loadout screen the carried
    // set changes between deployments, and building the LMG's belt or the sniper's
    // scope the first time someone equips it would hitch at exactly the moment the
    // player is looking straight at it.
    this.viewModel.warmup(ALL_WEAPONS.map((id) => WEAPONS[id]?.model ?? id));
  }

  /**
   * Point every weapon at a model builder that actually exists.
   *
   * Each of the 23 weapons asks for a builder named after itself. `buildWeaponModel`
   * throws for one it does not have, and it is called from `setWeapon` and from
   * `warmup` — so a single missing builder takes the whole game down at construction
   * time rather than showing the wrong gun. That is the correct behaviour for a
   * finished roster and the wrong one while the models are still being built, since
   * it makes the weapon table untestable until the last mesh lands.
   *
   * So each id is probed once, here, and a weapon whose own builder is missing falls
   * back to its class archetype (`modelFallback`). The moment a real builder is
   * registered this stops firing for that weapon, with no edit needed on either
   * side. A weapon whose fallback is missing too is left alone to throw, because at
   * that point something is genuinely wrong with the model registry.
   */
  _resolveModels() {
    const missing = [];
    for (const id of ALL_WEAPONS) {
      const d = WEAPONS[id];
      if (!d) continue;
      const want = d.model ?? id;
      try {
        this.viewModel.getModel(want);
      } catch {
        if (!d.modelFallback || d.modelFallback === want) continue;
        d.model = d.modelFallback;
        missing.push(id);
      }
    }
    if (missing.length) {
      console.warn(
        `[weapons] no model builder yet for ${missing.length} weapon(s); ` +
          `falling back to the class archetype: ${missing.join(', ')}`
      );
      // The fallbacks above rewrote `model` on the *base* table, and the slots were
      // resolved from it before that happened. Drop the cache so the effective defs
      // pick the new builder up; `effective()` mutates the same object in place, so
      // the slots' `def` references stay valid.
      this.attachments.cache.invalidate();
      for (const s of this.slots) s.def = this.attachments.effective(s.id);
    }
  }

  /** An inventory slot: full magazine, starting reserve, pattern at the top. */
  _makeSlot(id) {
    const def = this.attachments.effective(id);
    return { id, def, mag: def.magSize, reserve: def.startReserve, shotIndex: 0, bloom: 0 };
  }

  /**
   * A weapon's build changed. Re-point the slot at the resolved definition.
   *
   * `effective()` mutates one stable object per weapon rather than returning a new
   * one, so `slot.def` and the reference `ViewModel` is holding are already correct
   * by the time this runs. What is *not* automatic is the handful of values other
   * modules cached off the definition when it was last set — the input module's ADS
   * sensitivity, the camera's ADS field of view, and the viewmodel's kick constants,
   * all of which are read once in `setWeapon`. Those are refreshed here.
   *
   * Ammunition is deliberately left alone except for the reserve ceiling. A build
   * change must never create or destroy rounds: a magazine that is now larger than
   * the one in the weapon simply has room in it, and one that is now smaller drains
   * to the new size on its own. Clamping the loaded magazine down would silently
   * delete the difference, which is exactly the kind of leak the ammunition fuzz
   * exists to catch.
   */
  _onBuildChanged(weaponId) {
    const slot = this.slots.find((s) => s.id === weaponId);
    if (!slot) return;
    slot.def = this.attachments.effective(weaponId);
    slot.reserve = Math.min(slot.reserve, slot.def.maxReserve);
    if (slot === this.current) {
      this._applyWeaponSettings();
      this.viewModel.setWeapon(slot.def);
      this._emitAmmo();
      this._emitEffective();
    }
  }

  /** Publish the resolved stats of the weapon in hand. Consumed by the HUD and AI. */
  _emitEffective() {
    const d = this.def;
    this.events.emit('weapon:effective', {
      weapon: this.current.id,
      def: d,
      attachments: d.attachments ?? [],
    });
    this._pushBehaviours();
  }

  /**
   * Hand the behaviour systems the configuration this build implies.
   *
   * `src/game/behaviours/` owns minimap signature, flinch, overheat, the underbarrel
   * launcher, thermal and the laser, and it deliberately knows nothing about
   * attachments. Its published cycle is `reset()` then `apply(patch)` on every
   * loadout change, and this is the caller of it. The translation itself lives in
   * `attachments/index.js` — what a part *is* belongs to the gunsmith; what a
   * behaviour *does* belongs to them.
   *
   * Guarded twice over. The behaviour module is optional and, when it does exist, is
   * constructed after this one — so the identity check in `update` re-pushes the
   * moment it appears, and a throw on their side must not take a shot with it.
   */
  _pushBehaviours() {
    const beh = this.ctx.behaviours;
    if (!beh || typeof beh.apply !== 'function') return;
    this._behaviourSys = beh;
    try {
      beh.reset();
      beh.apply(behaviourPatch(this.def));
    } catch (err) {
      console.warn('[weapons] behaviour systems refused the attachment config', err);
    }
  }

  /** The weapon table, so other modules can read definitions without importing it. */
  get defs() {
    return WEAPONS;
  }

  /** Ids currently carried, primary first. */
  get loadout() {
    return this.slots.map((s) => s.id);
  }

  /* ------------------------------------------------------------------------ */

  get current() {
    return this.slots[this.index];
  }
  get def() {
    return this.slots[this.index].def;
  }

  /**
   * How far the sight has actually come up, 0..1.
   *
   * Everything that gates on "is the player aiming" reads this, never `input.ads` or
   * `player.ads`. The button is instantaneous; the gun is not, and the gap between
   * them is 235 ms of free pinpoint accuracy if anything keys off the boolean.
   */
  get adsProgress() {
    const vm = this.viewModel;
    if (vm && Number.isFinite(vm.adsRaw)) return vm.adsRaw < 0 ? 0 : vm.adsRaw > 1 ? 1 : vm.adsRaw;
    return this.player?.ads ? 1 : 0;
  }

  /** Triangle counts per built weapon — used by the perf overlay and the report. */
  get modelStats() {
    const out = {};
    for (const s of this.slots) {
      const m = this.viewModel.models.get(s.def.model ?? s.id);
      if (m) out[s.id] = m.triangles;
    }
    return out;
  }

  _applyWeaponSettings() {
    const d = this.def;
    // These two hooks exist on the input and camera modules precisely so weapons can
    // drive them; guarded because both are owned elsewhere.
    if (this.ctx.input) this.ctx.input.adsSensScale = d.ads.sensScale;
    if (this.ctx.cameraRig) this.ctx.cameraRig.adsFovScale = d.ads.fovScale;
  }

  _emitAmmo() {
    const c = this.current;
    this.events.emit('weapon:ammo', {
      mag: c.mag,
      reserve: c.reserve,
      max: c.def.magSize,
      weapon: c.id,
      name: c.def.name,
      className: c.def.className,
      fireMode: c.def.fireMode,
    });
  }

  /* ------------------------------------------------------------------------ */
  /* update                                                                    */
  /* ------------------------------------------------------------------------ */

  update(dt, elapsed) {
    this._time = elapsed;
    const input = this.input;
    const p = this.player;
    if (!input || !p) return;

    if (!this._announced) {
      this._announced = true;
      this._adoptSavedLoadout();
      this.events.emit('weapon:switch', { from: null, to: this.current.id, def: this.def });
      this._emitAmmo();
      this._emitEffective();
    }

    // The behaviour systems are built after this module and may not have existed at
    // the announce. One identity compare a frame catches the moment they appear.
    if (this.ctx.behaviours && this.ctx.behaviours !== this._behaviourSys) this._pushBehaviours();

    // Bloom decays continuously; it is what makes sustained hip fire punishing.
    const c = this.current;
    c.bloom = Math.max(0, c.bloom - c.def.spread.bloomDecay * dt);
    if (elapsed - this.lastShotAt > TUNING.patternResetTime) c.shotIndex = 0;

    // Before the dead check: a corpse's aim still has to settle. Sway is advanced
    // first so the servo publishes recoil and sway as one correction.
    this._updateAimSway(dt, elapsed);
    this._updateAimRecoil(dt, elapsed);

    if (p.dead) return;

    // Sprinting stows the weapon; you cannot fire until it is back up.
    if (p.sprinting || p.tacSprinting || p.state === 1) {
      this.sprintOutUntil = elapsed + this.def.ergonomics.sprintOut;
    }
    const ready = elapsed >= this.sprintOutUntil - TUNING.sprintOutSlack;

    this._updateState(elapsed);
    this._handleSwitching(input, elapsed);
    this._handleReload(input, elapsed);
    this._handleFire(input, elapsed, ready);

    if (input.pressedThisFrame?.has(TUNING.inspectKey) && this.state === STATE.IDLE) {
      this.viewModel.playInspect(this.def);
    }
  }

  /* ------------------------------- state clock ----------------------------- */

  _updateState(now) {
    if (this.state === STATE.IDLE) return;

    // The model swap happens at the bottom of the lowering arc. It runs off the
    // engine clock rather than a wall-clock timer so a frame hitch can never leave
    // the animation and the inventory out of step.
    if (this.state === STATE.SWITCHING && !this.switchSwapped && now >= this.switchSwapAt) {
      this.switchSwapped = true;
      this._completeSwitch(now);
    }

    if (now < this.stateUntil) return;

    if (this.state === STATE.RELOADING) {
      this._finishReloadStep(now);
    } else if (this.state === STATE.SWITCHING) {
      this.state = STATE.IDLE;
    } else if (this.state === STATE.PUMPING) {
      this.state = STATE.IDLE;
    } else if (this.state === STATE.CYCLING) {
      this.state = STATE.IDLE;
    }
  }

  /* -------------------------------- switching ------------------------------ */

  _handleSwitching(input, now) {
    if (this.state === STATE.SWITCHING) return;
    let target = -1;
    for (let i = 0; i < Math.min(3, this.slots.length); i++) {
      if (input.actionPressed(`slot${i + 1}`)) target = i;
    }
    if (input.actionPressed('swap')) target = (this.index + 1) % this.slots.length;
    if (input.mouse?.wheel) target = (this.index + this.slots.length + Math.sign(input.mouse.wheel)) % this.slots.length;
    if (target < 0 || target === this.index) return;

    this._switchTo(target, now);
  }

  _switchTo(target, now) {
    const outT = this.def.ergonomics.switchOut;
    const inT = this.slots[target].def.ergonomics.switchIn;

    this.state = STATE.SWITCHING;
    this.switchFrom = this.current.id;
    this.switchTarget = target;
    this.switchIn = inT;
    this.switchSwapAt = now + outT;
    this.switchSwapped = false;
    this.stateUntil = now + outT + inT;
    this.reloadPhase = null;
    this.pendingFire = false;
    this.burstLeft = 0;
    this.viewModel.cancelAnim();
    this.viewModel.playSwitch(this.def, -1, outT);
    this.events.emit('audio:play', { id: 'weapon_switch', volume: 0.65 });
  }

  _completeSwitch(now) {
    this.index = this.switchTarget;
    this._applyWeaponSettings();
    this.viewModel.setWeapon(this.def);
    this.viewModel.playSwitch(this.def, 1, this.switchIn);
    this.nextShotAt = Math.max(this.nextShotAt, this.stateUntil);
    this.events.emit('weapon:switch', { from: this.switchFrom, to: this.current.id, def: this.def });
    this._emitAmmo();
    this._emitEffective();
    void now;
  }

  /* --------------------------------- reload -------------------------------- */

  _handleReload(input, now) {
    if (this.state === STATE.SWITCHING) return;
    // A bolt in the middle of its stroke cannot also be having its magazine changed.
    // Without this the reload overwrites `state` and the cycle silently never ends —
    // the weapon would come out of the reload with a round chambered it never fed.
    if (this.state === STATE.CYCLING) return;
    const c = this.current;
    const d = c.def;

    if (this.state === STATE.RELOADING) {
      // Shotgun reloads are interruptible: the trigger cuts straight to the rack and
      // the shot is *queued*, so it lands the instant the rack finishes.
      //
      // Both halves matter. Queuing is what makes the promise true — cutting the
      // reload short without firing is worse than not interrupting at all, because
      // the player gave up their reload and got nothing. And the rack has to be a
      // one-way transition (see `_beginShellEnd`): re-entering it on every trigger
      // press pushed `stateUntil` forward each time, which left the weapon unable to
      // either fire or finish reloading for as long as the player kept trying.
      if (TUNING.interruptibleReload && d.shellByShell && c.mag > 0 && input.firePressed) {
        this.pendingFire = true;
        this._beginShellEnd(now);
      }
      return;
    }

    const wants = input.actionPressed('reload');
    const canReload = c.mag < d.magSize && c.reserve > 0;
    if (!wants || !canReload) return;
    this._beginReload(now);
  }

  _beginReload(now) {
    const c = this.current;
    const d = c.def;
    this.burstLeft = 0;

    if (d.shellByShell) {
      this.state = STATE.RELOADING;
      this.reloadPhase = 'start';
      this.stateUntil = now + d.reload.start;
      this.viewModel.playReloadStage('shellStart', d);
      this.events.emit('weapon:reload', { weapon: c.id, stage: 'start', empty: c.mag === 0 });
      this.events.emit('audio:play', { id: 'reload_start', volume: 0.7 });
      return;
    }

    const empty = c.mag === 0;
    const dur = empty ? d.reload.empty : d.reload.tactical;
    this.state = STATE.RELOADING;
    this.reloadPhase = 'mag';
    this.stateUntil = now + dur;
    this.viewModel.playReload(d, empty);
    this.events.emit('weapon:reload', { weapon: c.id, stage: 'start', empty });
    this.events.emit('audio:play', { id: empty ? 'reload_empty' : 'reload_tac', volume: 0.55 });
  }

  _finishReloadStep(now) {
    const c = this.current;
    const d = c.def;

    if (!d.shellByShell) {
      // A chambered round survives a tactical reload, so a well-timed reload gives
      // magSize + 1. Small detail, and players absolutely notice it.
      const target = d.magSize + (d.chambered && c.mag > 0 ? 1 : 0);
      const take = Math.min(c.reserve, target - c.mag);
      c.mag += take;
      c.reserve -= take;
      this.state = STATE.IDLE;
      this.reloadPhase = null;
      this.nextShotAt = Math.max(this.nextShotAt, now);
      this.events.emit('weapon:reload', { weapon: c.id, stage: 'end' });
      this._emitAmmo();
      return;
    }

    /* ----------------------------- shell by shell ---------------------------- */
    if (this.reloadPhase === 'start' || this.reloadPhase === 'shell') {
      if (this.reloadPhase === 'shell') {
        // Both sides of the transfer or neither: a clamped mag with an unclamped
        // reserve is how ammo quietly goes missing.
        if (c.mag < d.magSize && c.reserve > 0) {
          c.mag++;
          c.reserve--;
          this._emitAmmo();
        }
      }
      if (c.mag < d.magSize && c.reserve > 0) {
        this.reloadPhase = 'shell';
        this.stateUntil = now + d.reload.tactical;
        this.viewModel.playReload(d, false);
      } else {
        this._beginShellEnd(now);
      }
      return;
    }

    if (this.reloadPhase === 'end') {
      this.state = STATE.IDLE;
      this.reloadPhase = null;
      this.nextShotAt = Math.max(this.nextShotAt, now);
      this.events.emit('weapon:reload', { weapon: c.id, stage: 'end' });
      this._emitAmmo();
    }
  }

  _beginShellEnd(now) {
    // Racking is one-way. Anything that can be triggered repeatedly — and the
    // trigger itself can — must not be able to restart it and push the finish line
    // out again.
    if (this.reloadPhase === 'end') return;
    const d = this.def;
    this.reloadPhase = 'end';
    this.state = STATE.RELOADING;
    this.stateUntil = now + d.reload.end;
    this.viewModel.playReloadStage('shellEnd', d);
  }

  /* ---------------------------------- fire --------------------------------- */

  _handleFire(input, now, ready) {
    const c = this.current;
    const d = c.def;

    // A shot queued by interrupting a shell-by-shell reload fires the moment the
    // rack finishes. It does not need the trigger to still be held: the player
    // already spent their reload on it.
    if (this.pendingFire) {
      if (this.state === STATE.RELOADING) return; // still racking
      this.pendingFire = false;
      if (ready && c.mag > 0 && this.state === STATE.IDLE && now >= this.nextShotAt) {
        this._shoot(now);
        return;
      }
    }

    if (this.state !== STATE.IDLE) return;

    // Burst continuation runs even after the trigger is released.
    if (this.burstLeft > 0 && now >= this.burstNextAt) {
      if (c.mag > 0) {
        this._shoot(now);
        this.burstLeft--;
        this.burstNextAt = now + shotInterval(d);
        // Dead time between bursts. `rpm` is the *in-burst* rate for these weapons —
        // 947 for the M8A1 — and all four burst weapons on the roster put more than
        // 100 damage into one pull, so without a gap after the last round they would
        // be full-autos that happen to stutter, and the fastest killers in the game
        // by a distance. Applied on the burst's final round so the gap is between
        // bursts and not inside one.
        if (this.burstLeft === 0 && d.burstDelay > 0) {
          this.nextShotAt = Math.max(this.nextShotAt, now + d.burstDelay);
        }
      } else {
        this.burstLeft = 0;
      }
      return;
    }

    let wants = false;
    switch (d.fireMode) {
      case 'auto':
        wants = input.fire;
        break;
      case 'semi':
      case 'pump':
      case 'bolt':
        wants = input.firePressed;
        break;
      case 'burst':
        wants = input.firePressed;
        break;
      default:
        wants = input.firePressed;
    }
    if (!wants) {
      this.semiLatch = false;
      return;
    }
    if (!ready) return;
    // An external reason not to fire — an overheat lockout, most obviously. Checked
    // after the trigger is read so the latch and the dry-fire click still behave, and
    // before the ammunition check so a locked-out weapon does not also burn a round.
    if (this.fireInhibit) return;
    if (now < this.nextShotAt) return;

    if (c.mag <= 0) {
      if (!this.semiLatch) {
        this.semiLatch = true;
        this.events.emit('audio:play', { id: d.audio?.dry ?? 'dryfire', volume: 0.5 });
        if (TUNING.autoReloadOnEmpty && c.reserve > 0 && this.state === STATE.IDLE) this._beginReload(now);
      }
      return;
    }

    if (d.fireMode === 'burst') {
      this.burstLeft = (d.burstCount ?? 3) - 1;
      this.burstNextAt = now + shotInterval(d);
    }
    this._shoot(now);
  }

  _shoot(now) {
    const c = this.current;
    const d = c.def;
    const p = this.player;
    const cam = this.ctx.camera;
    cam.updateMatrixWorld();

    c.mag--;
    this.lastShotAt = now;
    this.nextShotAt = now + shotInterval(d);

    /* ------------------------------ aim solve ------------------------------ */
    _origin.setFromMatrixPosition(cam.matrixWorld);
    _dir.set(0, 0, -1).applyQuaternion(cam.quaternion).normalize();

    // How far the sight has come up, not whether the button is down: everything the
    // shot costs scales with the animation the player can see.
    const adsT = this.adsProgress;
    const st = {
      ads: p.ads,
      adsT,
      speed: p.speed,
      sprinting: p.sprinting,
      crouching: p.crouching,
      grounded: p.grounded,
    };
    const cone = Ballistics.coneFor(d, st, c.bloom);

    const results = this.ballistics.fire({
      weapon: d,
      origin: _origin,
      dir: _dir,
      cone,
      // Passed so a choke can tighten the *shell's* pattern as the stock comes into
      // the shoulder. The aim cone is a separate quantity and is still exactly zero
      // at this end of the transition; see `Ballistics.fire`.
      adsT,
      shooter: p,
      rng: this.ctx.rng,
    });

    c.bloom = Math.min(d.spread.maxBloom, c.bloom + d.spread.perShot);

    /* -------------------------------- recoil ------------------------------- */
    this._applyRecoil(d, c, p, adsT);

    /* ----------------------------- presentation ---------------------------- */
    this.viewModel.onFire(d);
    if (d.fireMode === 'pump') {
      this.state = STATE.PUMPING;
      this.stateUntil = now + d.pumpTime;
      this.nextShotAt = Math.max(this.nextShotAt, now + d.pumpTime);
      this.viewModel.playPump(d);
    } else if (d.fireMode === 'bolt') {
      // The lockout *is* the weapon. Holding the state for the whole cycle is what
      // makes a missed shot cost a second and a half rather than a trigger pull, and
      // it is the only thing separating this from a slow semi-auto.
      const cycle = d.boltCycle ?? 1.0;
      this.state = STATE.CYCLING;
      this.stateUntil = now + cycle;
      this.nextShotAt = Math.max(this.nextShotAt, now + cycle);
      this.viewModel.playBoltCycle(d);
    }

    // Muzzle position for tracers: the FX layer needs a world-space start point
    // that matches what the player can see, not the camera origin.
    _muzzle.copy(_origin).addScaledVector(_dir, 0.55);
    const vmMuzzle = this.viewModel.worldMuzzle(_muzzle);

    this.events.emit('weapon:fire', {
      weapon: c.id,
      def: d,
      origin: _origin.clone(),
      dir: _dir.clone(),
      spread: cone,
      muzzle: vmMuzzle,
      pellets: results.map((r) => ({ end: r.end, hit: r.hit, actor: r.actor, delayed: !!r.delayed })),
      ads: p.ads,
      adsT,
    });
    // `pitchBase` lets a weapon borrow another's voice at a different size. The audio
    // module's gun presets are its own to author and unknown ids fail silently by
    // contract, so a new weapon either borrows or is mute.
    this.events.emit('audio:play', {
      id: d.audio?.fire ?? 'fire_ar',
      volume: 1,
      pitch: (d.audio?.pitchBase ?? 1) * (0.97 + Math.random() * 0.06),
    });
    this._emitAmmo();
  }

  _applyRecoil(d, c, p, adsT) {
    const rng = this.ctx.rng;
    const pat = d.pattern[Math.min(c.shotIndex, d.pattern.length - 1)];
    c.shotIndex++;

    const r = d.recoil;
    let pitch = r.pitch * pat[0];
    let yaw = r.yaw * pat[1];

    // The noise term is a fraction of the deterministic magnitude — enough to stop
    // the pattern looking mechanical, never enough to hide it.
    pitch *= 1 + (rng() * 2 - 1) * r.random;
    yaw += r.yaw * (rng() * 2 - 1) * r.random * 0.6;

    let scale = 1;
    // Bracing arrives with the sight, not with the button. Snapping the full ADS
    // discount on one frame after RMB is the same free lunch as snapping the cone.
    scale *= THREE.MathUtils.lerp(1, r.adsScale, adsT);
    if (p.crouching) scale *= r.crouchScale;
    // Sliding. Currently unreachable in play: a slide re-arms `sprintOutUntil` every
    // frame, so `ready` is false for the whole slide and for `sprintOut` afterwards.
    // Kept because the day sliding lets you fire, this is the number it needs.
    if (p.state === 1) scale *= 1.25;
    if (!p.grounded) scale *= 1.30;

    // The pattern entry is an increment on the climb so far, not an absolute offset.
    this.aimPitch += pitch * scale;
    this.aimYaw += yaw * scale;
    this._publishRecoil(r.kick * THREE.MathUtils.lerp(1, 0.72, adsT), pitch * scale, yaw * scale);
  }

  /* ------------------------------- aim recoil ------------------------------- */

  /**
   * Advance the aim-recoil curve and keep the camera rig sitting on it.
   *
   * The climb is *held* for a beat after each shot before it starts returning, which
   * is the whole mechanism: with the hold longer than the shot interval, shot N
   * stacks on shots 1..N-1 and the pattern's running sum is what the player fights.
   * The hold is scaled off the weapon's own fire rate so this holds for a 900 RPM
   * SMG and a 340 RPM marksman rifle alike, and clamped so a pump shotgun — whose
   * cycle is longer than any sane hold — kicks from a settled aim every shell.
   */
  _updateAimRecoil(dt, now) {
    const hold = Math.min(
      TUNING.recoilHoldMax,
      Math.max(TUNING.recoilHoldMin, shotInterval(this.def) * TUNING.recoilHoldShots)
    );
    if (now - this.lastShotAt > hold) {
      const k = Math.exp(-TUNING.recoilRecovery * dt);
      this.aimPitch *= k;
      this.aimYaw *= k;
      if (Math.abs(this.aimPitch) < 1e-7) this.aimPitch = 0;
      if (Math.abs(this.aimYaw) < 1e-7) this.aimYaw = 0;
    }
    this._publishRecoil(0, 0, 0);
  }

  /* -------------------------------- aim sway -------------------------------- */

  /**
   * Scoped idle sway, and the breath that steadies it.
   *
   * This moves the *point of aim*, not the viewmodel. That distinction is the whole
   * design: swaying the weapon in view space would look identical and be a lie,
   * because the ADS pose is solved from `aimPoint` and the shot comes off the camera
   * axis — the sight would wander while every bullet still landed dead centre. Going
   * through the same rig servo the recoil pattern uses means the sight and the
   * crosshair stay welded together and the world drifts under them, which is what
   * holding a heavy rifle steady actually feels like.
   *
   * Spread is untouched. Fully-ADS and stationary is still exactly zero cone; the
   * uncertainty a sniper fights is *where the crosshair is pointing*, not whether
   * the bullet goes where it points.
   *
   * The motion is a Lissajous figure — pitch at f, yaw at f/2 — so the muzzle traces
   * a slow figure-eight rather than a circle. A circle is predictable enough to lead
   * and a random walk cannot be timed at all; a figure-eight has two moments per lap
   * where it is nearly still, and finding them is the skill.
   */
  _updateAimSway(dt, now) {
    const cfg = this.def.aimSway;
    const p = this.player;
    if (!cfg || !p) {
      this.swayPitch = 0;
      this.swayYaw = 0;
      this.swayScale = 1;
      this.breathLeft = 1;
      this.breathHeld = false;
      return;
    }

    const adsT = this.adsProgress;
    // Only when actually scoped and nearly still: sway on top of a sprint would be
    // noise on top of noise, and the player is not aiming during either.
    const still = 1 - THREE.MathUtils.clamp(p.speed / 1.8, 0, 1);
    const weight = adsT * adsT * still * (p.dead ? 0 : 1);

    /* ------------------------------ breath state ----------------------------- */
    const wantHold =
      !!this.input?.action?.(TUNING.holdBreathAction) && adsT > 0.55 && !p.sprinting && !p.dead;
    const canHold = this.breathLeft > 0;
    this.breathHeld = wantHold && canHold;

    if (this.breathHeld) {
      this.breathLeft = Math.max(0, this.breathLeft - dt / cfg.holdTime);
      // Running out is not the same as letting go: the lungs give up and the sight
      // swings wider than it started for a beat. That is the cost of holding too long.
      if (this.breathLeft === 0) this.gaspUntil = now + cfg.gaspTime;
    } else {
      this.breathLeft = Math.min(1, this.breathLeft + (dt * cfg.recover) / cfg.holdTime);
    }

    let target = 1;
    if (this.breathHeld) target = cfg.holdScale;
    else if (now < this.gaspUntil) target = cfg.gaspScale;
    // `holdIn` governs settling; releasing is deliberately about twice as fast,
    // because the sight coming back is a consequence and not a skill.
    const rate = this.breathHeld ? 1 / Math.max(0.02, cfg.holdIn) : 2 / Math.max(0.02, cfg.holdIn);
    this.swayScale += (target - this.swayScale) * Math.min(1, rate * dt);

    /* ------------------------------- the figure ------------------------------ */
    this.swayPhase += dt * cfg.freq * Math.PI * 2;
    const amp = cfg.amp * weight * this.swayScale;
    this.swayPitch = Math.sin(this.swayPhase) * amp;
    this.swayYaw = Math.sin(this.swayPhase * 0.5 + 0.6) * amp * (cfg.yawScale ?? 1);
  }

  /**
   * Publish aim recoil as a delta onto the camera rig's accumulator.
   *
   * `weapon:recoil` is additive at the far end, and the rig owns how fast its
   * accumulator decays. That rate is not ours to set and is currently far too fast
   * for a pattern to stack — at 725 RPM it eats over half the climb between two
   * shots, which pins the rifle at a per-shot equilibrium instead of letting it walk.
   * So this module keeps the authoritative curve and publishes only the difference
   * needed to move the rig onto it, every frame the curve is live.
   *
   * The difference is measured against the rig's *actual* value rather than a local
   * model of its decay. That costs one property read and buys correctness under any
   * recovery rate the rig picks, including after it is retuned — a hardcoded copy of
   * someone else's constant would silently over- or under-shoot the day it changes.
   * With no rig to read, we degrade to publishing the raw per-shot impulse.
   */
  _publishRecoil(kick, incPitch, incYaw) {
    const rig = this.ctx.cameraRig;
    const servo = rig && Number.isFinite(rig.recoilTargetPitch) && Number.isFinite(rig.recoilTargetYaw);
    // Recoil and sway ride the same servo. `aimPitch` stays the pure pattern
    // accumulator — sway is added only here, at the point of publication, so the
    // pattern remains measurable in isolation.
    const pitch = servo ? this.aimPitch + this.swayPitch - rig.recoilTargetPitch : incPitch;
    const yaw = servo ? this.aimYaw + this.swayYaw - rig.recoilTargetYaw : incYaw;
    // Nothing to correct and nothing to shake: stay off the bus.
    if (kick === 0 && Math.abs(pitch) < 1e-7 && Math.abs(yaw) < 1e-7) return;
    this.events.emit('weapon:recoil', { pitch, yaw, kick, sustain: kick === 0 });
  }

  /* --------------------------------- loadout -------------------------------- */

  /**
   * Equip a loadout chosen elsewhere.
   *
   * The progression module calls this directly *and* emits `loadout:set`; both land
   * here, so the first thing this does is detect that nothing changed and return.
   * Idempotence is not a nicety — without it every loadout confirmation would run
   * the swap twice and the second one would cancel the first one's draw animation.
   *
   * Unknown ids are dropped rather than throwing. This is called from a module that
   * wraps it in try/catch precisely because it cannot be sure what exists yet, and a
   * loadout screen that can crash the weapon system is worse than one that quietly
   * gives you the rifle.
   *
   * What it will not do:
   *  - corrupt a reload. Ammunition only ever moves in `_finishReloadStep`, and if
   *    the weapon being reloaded is still in the loadout its slot object survives
   *    untouched, so the reload simply carries on. If it is *not*, the reload is
   *    abandoned before any transfer, which loses nothing.
   *  - fire on the swap frame. The weapon goes into SWITCHING, which `_handleFire`
   *    refuses outright, and `nextShotAt` is pushed past the end of the draw.
   *
   * @returns {boolean} whether the loadout is now the requested one
   */
  setLoadout(primaryId, secondaryId) {
    const ids = [];
    for (const raw of [primaryId, secondaryId]) {
      if (!raw) continue;
      if (!WEAPONS[raw]) {
        console.warn(`[weapons] setLoadout: no weapon "${raw}"`);
        continue;
      }
      if (!ids.includes(raw)) ids.push(raw);
    }
    if (!ids.length) return false;

    const same = ids.length === this.slots.length && ids.every((id, i) => this.slots[i].id === id);
    if (same) return true;

    // Slot objects are carried across by id, so a weapon that stays in the loadout
    // keeps its magazine, its reserve and its place in the recoil pattern. Losing
    // that on every visit to the loadout screen would be a free reload.
    const prev = new Map(this.slots.map((s) => [s.id, s]));
    const held = this.current.id;
    const primaryChanged = this.slots[0]?.id !== ids[0];
    const next = ids.map((id) => prev.get(id) ?? this._makeSlot(id));
    const keep = next.findIndex((s) => s.id === held);

    /**
     * Which weapon ends up in the player's hands.
     *
     * The primary, whenever the primary is what changed — otherwise picking a rifle
     * on the pre-deploy screen while carrying the old one as a secondary would spawn
     * the player holding the *secondary*, which is precisely the bug the loadout
     * screen exists to prevent.
     *
     * But not otherwise. Editing the second slot must not snatch the weapon out of a
     * player's hands mid-firefight, and swapping the two slots around means they are
     * already holding the new primary and nothing needs to move at all.
     */
    const equipPrimary = keep < 0 || (primaryChanged && held !== ids[0]);

    this.slots = next;
    this.index = equipPrimary ? 0 : keep;

    if (!equipPrimary && this.state !== STATE.SWITCHING) {
      this._emitAmmo();
      return true;
    }
    this._equipInHand(held, this._time);
    return true;
  }

  /**
   * Put the current slot in the player's hands, cancelling whatever was happening.
   *
   * Used only by `setLoadout`. It is not `_switchTo`: that one animates a holster of
   * the outgoing weapon and swaps at the bottom of the arc, which cannot work when
   * the outgoing weapon is no longer in the inventory to animate.
   */
  _equipInHand(fromId, now) {
    const d = this.def;

    // Before the first frame there is nothing to animate and no clock to animate it
    // on. Equip silently; the announce in `update` emits the events.
    if (!this._announced) {
      this.state = STATE.IDLE;
      this.stateUntil = 0;
      this.reloadPhase = null;
      this.pendingFire = false;
      this.burstLeft = 0;
      this._applyWeaponSettings();
      this.viewModel.cancelAnim();
      this.viewModel.setWeapon(d);
      return;
    }

    const inT = Math.max(TUNING.loadoutDrawMin, d.ergonomics.switchIn);
    this.state = STATE.SWITCHING;
    this.switchFrom = fromId;
    this.switchTarget = this.index;
    this.switchIn = inT;
    this.switchSwapAt = now;
    this.switchSwapped = true; // the swap already happened; only the draw is left
    this.stateUntil = now + inT;
    this.reloadPhase = null;
    this.pendingFire = false;
    this.burstLeft = 0;
    this.semiLatch = false;
    this.nextShotAt = Math.max(this.nextShotAt, this.stateUntil);
    // A fresh weapon does not inherit the last one's climb or its place in the
    // pattern; both belong to the gun, not to the player.
    this.aimPitch = 0;
    this.aimYaw = 0;
    this.current.shotIndex = 0;
    this.swayScale = 1;
    this.breathLeft = 1;

    this._applyWeaponSettings();
    this.viewModel.cancelAnim();
    this.viewModel.setWeapon(d);
    this.viewModel.playSwitch(d, 1, inT);
    this.events.emit('weapon:switch', { from: fromId, to: this.current.id, def: d });
    this.events.emit('audio:play', { id: 'weapon_switch', volume: 0.65 });
    this._emitAmmo();
    this._emitEffective();
  }

  /**
   * Adopt a loadout that was decided before this module could hear about it.
   *
   * A saved loadout is restored from storage inside the progression module's
   * constructor and is never re-published, so without this the player would deploy
   * holding the default carry set and only get their own weapons after visiting the
   * loadout screen again. Read through `ctx` rather than by importing, and guarded:
   * progression is optional and this must work with it absent.
   */
  _adoptSavedLoadout() {
    if (this._adoptedLoadout) return;
    this._adoptedLoadout = true;
    try {
      const pr = this.ctx.progression;
      if (!pr || !pr.primary) return;
      this.setLoadout(pr.primary, pr.secondary);
    } catch (err) {
      console.warn('[weapons] could not adopt the saved loadout', err);
    }
  }

  /* --------------------------------- helpers -------------------------------- */

  /** Give ammo (pickups). Returns how much was actually taken. */
  addAmmo(weaponId, rounds) {
    const s = this.slots.find((x) => x.id === weaponId) ?? this.current;
    const take = Math.min(rounds, s.def.maxReserve - s.reserve);
    s.reserve += take;
    if (s === this.current) this._emitAmmo();
    return take;
  }

  refillAll() {
    for (const s of this.slots) {
      s.mag = s.def.magSize;
      s.reserve = s.def.startReserve;
    }
    this._emitAmmo();
  }

  dispose() {
    this._offLoadout?.();
    this._offAttachments?.();
    this.attachments?.dispose();
    this.viewModel.dispose();
  }
}
